/**
 * Built-in widgets shipped with the viewer itself (independent of stdlib).
 *
 * `arch-module` is the *default* widget — the canonical face of a module.
 * It is fully configurable through `widget.*` cascade fields so kinds can
 * reuse it with per-kind defaults instead of shipping bespoke custom
 * elements. Authors can still register a different element via
 * `cascade widget: my-element` when they want a fully custom visual.
 *
 * Configuration (all attributes are kebab-cased `widget.*` field names):
 *
 *   widget.icon          icon id (or inline svg) for the header glyph
 *   widget.color         stroke colour (CSS colour)
 *   widget.bg            background colour or CSS background image value
 *   widget.subheader     field/label path; resolved value renders under
 *                        the name (e.g. `team`, `aspect domain`)
 *   widget.footer        field/label path; resolved value renders as
 *                        the footer band
 *   widget.label1        field/label path; resolved value renders as
 *                        a chip in the body (key: value)
 *   widget.label2        second chip slot in the body
 *   widget.badge         short literal text shown as a corner badge pill
 *                        in the card's top-right (e.g. a cloud provider
 *                        tag like "AWS"). Empty → no badge.
 *   widget.badgeColor    badge accent colour (CSS colour). Defaults to
 *                        the card accent when unset.
 *
 * Body modes (auto-detected from `arch.children`):
 *   - subspace mode   (children.length > 0): body reserves space for the
 *                     renderer's subspace overlay; description + chips
 *                     omitted (they appear in the side panel).
 *   - leaf mode       (no children): body shows description on top, then
 *                     two chip rows. Empty chip rows collapse → the
 *                     description claims the freed space.
 *
 * `arch-template` is the host for inline-HTML widgets (modules with
 * `widget: "<html>"`). The shadow content is set by the React side via
 * `setTemplate(html)`.
 *
 * Both elements register idempotently — calling registerBuiltinWidgets
 * twice (StrictMode double-mount, hot reload) is a no-op the second time.
 */

import { renderDescriptionHtml } from "../description/render.js";
import { iconSvg } from "../generated/stdlib-widgets/arch.ui/icons.js";
import { splitCasedName, extractWidgetSpec, type Module } from "@archlang/scene/arch-model";
import {
  moduleToCardInput,
  composeBoardNode,
  NODE_W,
  NODE_H,
  setWidgetSizeRegistry,
  shapeSizeRegistry,
  customElementsRegistry,
  type BoardItem,
} from "@archlang/scene/board";
import { defaultTokens, mix, paletteColor, toHTML } from "@archlang/scene";
import type { LifecyclePaint } from "@archlang/scene/board";
import { themeTokens } from "./themeTokens.js";
import { subscribeTheme } from "../theme/theme.js";

const TAG = "arch-module";

/**
 * Light pre-processing applied to the description before markdown
 * rendering inside a widget body:
 *   - drop a leading `#`-style heading line so the widget body doesn't
 *     duplicate the module name (the heading is almost always the
 *     module's own name; the widget header already shows it).
 *   - collapse `[text](url)` markdown links to bare `text`. Widget
 *     bodies are too small to be a productive navigation surface and
 *     the underline noise hurts readability of the prose. The side
 *     panel still renders the links.
 */
function preprocessWidgetDescription(md: string): string {
  if (!md) return "";
  let out = md.replace(/^\s*#{1,6}\s+[^\n]*\n+/, "");
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1");
  return out;
}

let registered = false;

interface FieldEntryLike {
  readonly path: readonly string[];
  readonly value: unknown;
}

interface ArchLike {
  readonly name?: string;
  readonly typeName?: string;
  readonly description?: string;
  readonly fields?: readonly FieldEntryLike[];
  readonly aspects?: readonly FieldEntryLike[];
  readonly children?: readonly unknown[];
  /** Enclosing space (v0.5); shown as the node badge when no widget badge. */
  readonly space?: string;
  /** True when the module's name was derived from its type (§4.14, unnamed).
   *  Drives a subtle muted/italic name cue + tooltip on the card. */
  readonly unnamed?: boolean;
}

function valueText(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (typeof v === "object") {
    const o = v as { text?: unknown; value?: unknown };
    if (typeof o.text === "string") return o.text;
    if (typeof o.value === "string" || typeof o.value === "number") return String(o.value);
  }
  return "";
}

function pathEquals(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function findField(arch: ArchLike | null, path: readonly string[]): string {
  if (!arch?.fields) return "";
  for (const f of arch.fields) {
    if (pathEquals(f.path, path)) return valueText(f.value);
  }
  return "";
}

function findLabel(arch: ArchLike | null, path: readonly string[]): string {
  if (!arch?.aspects) return "";
  for (const l of arch.aspects) {
    if (pathEquals(l.path, path)) return valueText(l.value);
  }
  return "";
}

/* ─────────────── arch-module ─────────────── */

const STYLE = `
  :host {
    display: block;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    font: var(--au-font, 500 14px system-ui, -apple-system, "Segoe UI", sans-serif);
    color: var(--au-fg, #e2e8f0);
    cursor: pointer;
    --_accent: var(--arch-module-accent, var(--au-info, #60a5fa));
    --_bg: var(--arch-module-bg,
            linear-gradient(160deg,
              var(--au-bg-elev-2, #334155),
              var(--au-bg-elev-1, #1e293b)));
    --_radius: var(--arch-module-radius, var(--au-radius-md, 12px));
    --_border: 1px solid color-mix(in oklab, var(--_accent) 18%, transparent);
  }

  .card {
    width: 100%; height: 100%;
    box-sizing: border-box;
    display: flex; flex-direction: column;
    gap: var(--au-space-1, 0.25rem);
    padding: var(--au-space-2, 0.5rem) var(--au-space-3, 0.75rem);
    border-radius: var(--_radius);
    background: var(--_bg);
    border: var(--_border);
    box-shadow: var(--au-shadow-card,
      0 6px 20px -8px rgba(0,0,0,0.5),
      0 1px 0 0 rgba(255,255,255,0.04) inset);
    overflow: hidden;
    position: relative;
    transition:
      border-color var(--au-duration-fast, 120ms) var(--au-ease, cubic-bezier(0.4,0,0.2,1)),
      box-shadow var(--au-duration-fast, 120ms) var(--au-ease, cubic-bezier(0.4,0,0.2,1));
    /* Card hosts a size-typed container so any descendant can declare a
     * nested container and size its own text/icons against the actual
     * rendered region via cqh/cqw. Convention reusable across widgets. */
    container-type: size;
    container-name: archui-card;
  }

  /* Accent stripe along the top edge — subtle identity cue. */
  .card::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 2px;
    background: var(--_accent);
    opacity: 0.7;
    pointer-events: none;
  }

  /* Corner badge — provider/identity tag pinned to the card's top-right.
   * Reveals with the compact band so it doesn't clutter the dot-zoom
   * silhouette. --_badge is the badge accent (widget.badgeColor),
   * falling back to the card accent. */
  .badge {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    padding: 1px 0.5em;
    border-bottom-left-radius: var(--au-radius-sm, 6px);
    border-top-right-radius: var(--_radius);
    font-size: clamp(7px, 9cqh, 11px);
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1.5;
    color: var(--_badge, var(--_accent));
    background: color-mix(in oklab, var(--_badge, var(--_accent)) 20%, var(--au-bg, #0f172a));
    border: 1px solid color-mix(in oklab, var(--_badge, var(--_accent)) 50%, transparent);
    border-top: none;
    border-right: none;
    pointer-events: none;
    white-space: nowrap;
    max-width: 60%;
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: var(--arch-band-compact-reveal, 0);
  }
  .badge:empty { display: none; }

  :host([data-hovered="true"]) .card {
    border-color: color-mix(in oklab, var(--_accent) 55%, transparent);
  }
  :host([data-selected="true"]) .card {
    border-color: var(--au-info, #38bdf8);
    box-shadow:
      0 0 0 3px color-mix(in oklab, var(--au-info, #38bdf8) 25%, transparent),
      0 6px 24px -4px color-mix(in oklab, var(--au-info, #38bdf8) 40%, transparent);
  }
  :host([data-highlighted="true"]) .card {
    border-color: var(--au-warn, #facc15);
    box-shadow:
      0 0 0 2px color-mix(in oklab, var(--au-warn, #facc15) 45%, transparent),
      0 6px 20px -8px rgba(0,0,0,0.5);
  }
  :host([data-error="true"]) .card {
    border-color: var(--au-err, #f87171);
    box-shadow:
      0 0 0 2px color-mix(in oklab, var(--au-err, #f87171) 45%, transparent),
      0 6px 20px -8px rgba(0,0,0,0.5);
  }

  /* ─ Header ─
   *
   * Layout intent (pure-CSS): header takes the height it needs for one
   * line of name text (min-height anchor), body takes everything else
   * by flex-grow, and at low zoom the body's grow weight collapses
   * (gated by --arch-band-compact) so the header absorbs the leftover.
   *
   * Header is its own size-typed container so text/icon sizes track
   * its actual rendered height via cqh units (1cqh = 1% of header
   * height) — when the header gets taller, text grows automatically. */

  .header {
    flex: 1 0 auto;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    align-content: center;
    justify-content: center;
    /* cqh/cqw resolve against the CARD (no size-container on header —
     * that creates a circular dependency between header height and
     * its own font-driven content).
     *
     * Base font is a FIXED pixel size (independent of card size) so
     * the header content stays the same at full zoom regardless of
     * whether the card is 220px or 420px wide — bigger cards just
     * leave more room for the body. The cqh boost only kicks in as
     * band-compact-reveal drops toward zero (dot zoom), where the
     * header is absorbing the body's space and the text should grow to
     * fill it. Driven off the REVEAL phase (not the structure phase) so
     * the header stays large while the body slot opens empty (the
     * structure sub-band) and only shrinks as the body content fades in
     * (the reveal sub-band) — that keeps text and body in lockstep
     * instead of shrinking text into a still-empty card.
     * The cqw cap prevents single-line overflow on extremely narrow
     * cards. */
    font-size:
      clamp(
        8px,
        min(
          calc(14px + 22cqh * (1 - var(--arch-band-compact-reveal, 0))),
          16cqw
        ),
        96px
      );
    /* Tight — narrower than a space character. SVG icons carry their
     * own internal padding via viewBox so larger gaps look very far. */
    row-gap: 0.1em;
    column-gap: 0.15em;
    min-width: 0;
    overflow: hidden;
  }
  .icon {
    /* Inline so the icon flows in the title's text run — sits beside
     * the first word and only ends up on its own line when even
     * (icon + first-word) overflows the row. Width tracks the title
     * font (1em) so they stay visually paired. */
    display: inline-block;
    vertical-align: -0.15em;
    width: 1em;
    height: 1em;
    margin-right: 0.15em;
    color: var(--_accent);
  }
  /* Override the iconSvg() helper's hard-coded width=16 height=16
   * attributes so the SVG fills its container instead of staying at
   * a fixed 16px regardless of .icon's 1em sizing. The wrapper span
   * must be block-level / 100% so the SVG width-100% has a
   * containing block of non-zero size to resolve against — inline
   * spans give percentages no reference and the SVG falls back to
   * its attribute width. */
  .icon-default { display: block; width: 100%; height: 100%; }
  .icon svg { width: 100% !important; height: 100% !important; display: block; }
  .icon:has(slot[name="icon"]:not(:empty)) .icon-default { display: none; }

  .name-block {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    display: flex; flex-direction: column; gap: 0.1em;
    align-items: center;
    text-align: center;
  }
  .title-row {
    /* Inline flow (not flex) so the icon participates in text wrapping
     * like another word. Browser picks break-points that keep
     * icon + first word together when possible — flex-wrap would
     * always isolate the icon on its own line as a discrete flex
     * item once the row overflowed. */
    display: block;
    text-align: center;
    max-width: 100%;
    min-width: 0;
    line-height: 1.2;
  }
  .name {
    /* Inline so it flows beside the icon in .title-row (an inline-
     * flow container). The browser picks word-break points that keep
     * icon + first word together when possible. */
    display: inline;
    font-weight: 700;
    color: var(--au-fg, #f1f5f9);
    /* Name inherits the header's clamped font-size — driving the
     * clamp from one place (header) keeps icon, name and gaps in
     * lockstep. Setting an explicit font-size here would re-introduce
     * the cqh→header→cqh circular dependency. */
    font-size: inherit;
    /* Allow wrap by default. With splitCasedName running on the input,
     * long camel/acronym/snake identifiers already contain word
     * boundaries — so the browser wraps between words when possible
     * and breaks within a word only when a single word can't fit. */
    white-space: normal;
    overflow: hidden;
    line-height: 1.15;
    max-width: 100%;
    word-break: break-word;
    overflow-wrap: anywhere;
    text-align: center;
  }
  /* Unnamed module (§4.14): name derived from its type — muted + italic so
   * it reads as "not spelled out" without shouting. */
  :host([data-unnamed="true"]) .name {
    font-style: italic;
    opacity: 0.75;
  }
  .subheader {
    color: var(--au-fg-muted, #94a3b8);
    font-size: 0.6em;
    line-height: 1.1;
    letter-spacing: 0.02em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: var(--arch-band-compact-reveal, 0);
    max-height: calc(2em * var(--arch-band-compact, 0));
    max-width: 100%;
  }
  .subheader:empty { display: none; }

  /* ─ Body (mode-dependent) ─
   *
   * Body's grow is gated by --arch-band-compact so at dot zoom
   * the grow drops to zero — header (flex-grow 1) then absorbs all
   * the leftover space. At full zoom the body's huge grow weight
   * dominates, so body wins by an order of magnitude and the header
   * stays at its content (min-height) size. */

  .body {
    flex: calc(999 * var(--arch-band-compact, 0)) 0 0;
    min-height: 0;
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: var(--au-space-1, 0.25rem);
    container-type: size;
    container-name: archui-card-body;
  }
  /* Subspace overlay (mounted as sibling div by SpaceRenderer) arrives
   * with [body] — we don't render it inside the widget, but if a
   * consumer ever slots [body] content we let it show. */
  ::slotted([body]),
  ::slotted([slot="body"]) {
    display: block;
    opacity: var(--arch-band-compact-reveal, 0);
  }

  /* Description: present only in leaf mode AND only at full zoom.
   * Rendered as markdown via the same renderer the side panel uses,
   * so paragraph breaks, code, lists, etc. behave the same. Compact
   * arch-desc subset styled inline since shadow DOM doesn't inherit
   * document CSS. */
  .desc {
    flex: 0 1 auto;
    min-height: 0;
    color: var(--au-fg-soft, #cbd5e1);
    font-size: 0.68em;
    line-height: 1.45;
    /* Reveal at compact band — description shows as soon as body
     * opens, not held until full zoom. */
    opacity: var(--arch-band-compact-reveal, 0);
    max-height: calc(20em * var(--arch-band-compact, 0));
    overflow: hidden;
    word-break: break-word;
  }
  .desc:empty { display: none; }
  /* Compact markdown styling for shadow-DOM descriptions. Mirrors the
   * .arch-desc rules in index.css used by the side panel. */
  .desc > * + * { margin-top: 0.4em; }
  .desc p { margin: 0; }
  .desc .arch-desc-h {
    font-size: 1.05em;
    font-weight: 700;
    color: var(--au-fg, #f1f5f9);
    line-height: 1.2;
    margin: 0;
  }
  .desc .arch-desc-h:first-child { margin-top: 0; }
  .desc strong { color: var(--au-fg, #f1f5f9); font-weight: 600; }
  .desc em { font-style: italic; }
  .desc s { color: #64748b; text-decoration: line-through; }
  .desc code {
    font: var(--au-font-mono, 500 0.9em ui-monospace, "JetBrains Mono", Menlo, monospace);
    background: rgba(255,255,255,0.06);
    padding: 0 0.3em;
    border-radius: 3px;
    color: var(--au-accent, #34d399);
  }
  .desc pre {
    margin: 0;
    padding: 0.4em 0.6em;
    background: rgba(0,0,0,0.25);
    border-radius: 4px;
    overflow: auto;
    font: var(--au-font-mono, 500 0.85em ui-monospace, "JetBrains Mono", Menlo, monospace);
  }
  .desc pre code { background: transparent; padding: 0; color: inherit; }
  .desc a { color: var(--au-info, #7dd3fc); text-decoration: underline; }
  .desc a:hover { color: #bae6fd; }
  .desc ul, .desc ol { margin: 0; padding-left: 1.2em; }
  .desc ul { list-style: disc; }
  .desc ol { list-style: decimal; }
  .desc li { margin: 0.1em 0; }
  .desc blockquote {
    margin: 0;
    padding-left: 0.6em;
    border-left: 2px solid var(--_accent);
    color: var(--au-fg-muted, #94a3b8);
  }
  .desc hr { border: none; border-top: 1px solid rgba(255,255,255,0.08); margin: 0.4em 0; }
  .desc table { border-collapse: collapse; }
  .desc th, .desc td { padding: 2px 6px; border: 1px solid rgba(255,255,255,0.08); }

  /* Chip rows: one row per configured slot. Empty rows collapse. */
  .chip-row {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: var(--au-space-1, 0.25rem);
    min-width: 0;
    overflow: hidden;
    opacity: var(--arch-band-compact-reveal, 0);
  }
  .chip-row:empty { display: none; }
  .chip {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    padding: 1px 0.55em;
    border-radius: var(--au-radius-pill, 9999px);
    background: color-mix(in oklab, var(--_accent) 16%, transparent);
    font-size: 0.75em;
    line-height: 1.5;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chip-key {
    color: var(--au-fg-muted, #94a3b8);
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.04em;
    font-size: 0.85em;
  }
  .chip-value {
    color: var(--au-fg-soft, #cbd5e1);
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* In subspace mode the body is owned by the projected nested-space
   * content (via the default <slot> below) — hide local content
   * (description, chips). */
  :host([data-mode="subspace"]) .desc,
  :host([data-mode="subspace"]) .chip-row { display: none; }

  /* Subspace slot. Fills the body region exactly. Visible only when
   * the renderer has stamped data-has-subspace, so we don't reserve
   * pointer-events / space in leaf mode. */
  .subspace {
    position: absolute;
    inset: 0;
    display: none;
  }
  :host([data-mode="subspace"]) .subspace { display: block; }

  /* ─ Footer ─
   * Absolutely pinned to the bottom of the card so it stays put even
   * when the body's compact band shrinks. Band-full controls reveal
   * (opacity + max-height collapse). */

  .footer {
    flex: 0 0 auto;
    /* Reveal at compact band — footer appears alongside description
     * as soon as body opens, not held until full zoom. */
    max-height: calc(1.6em * var(--arch-band-compact, 0));
    overflow: hidden;
    opacity: var(--arch-band-compact-reveal, 0);
    font-size: 0.72em;
    color: var(--au-fg-muted, #94a3b8);
    letter-spacing: 0.02em;
    line-height: 1.3;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-align: left;
  }
  .footer:empty { display: none; }

  :host([data-empty="true"]) .card {
    padding: var(--au-space-1, 0.25rem) var(--au-space-2, 0.5rem);
  }
`;

class ArchModuleElement extends HTMLElement {
  /**
   * `widgetSize` resolver hook. arch-module is *customisable*: it reads
   * the instance's `widget.width` / `widget.height` props and returns
   * them when numeric. ArchLang-resolved modules always carry a value
   * (stamped from the `module` base kind's builtin defaults); standalone
   * uses of the element with no props fall through to the solver's
   * NODE_W / NODE_H constants.
   */
  static sizeFor(
    props: ReadonlyMap<string, unknown>,
  ): { w?: number; h?: number } {
    const w = props.get("width");
    const h = props.get("height");
    const out: { w?: number; h?: number } = {};
    if (typeof w === "number") out.w = w;
    if (typeof h === "number") out.h = h;
    return out;
  }

  static get observedAttributes() {
    return [
      "name",
      "icon",
      "color",
      "bg",
      "subheader",
      "footer",
      "label1",
      "label2",
      "badge",
      "badge-color",
      "data-has-subspace",
    ];
  }

  private archData: ArchLike | null = null;

  private built = false;
  private root: ShadowRoot | null = null;
  private elIcon!: HTMLDivElement;
  private elIconDefault!: HTMLSpanElement;
  private elName!: HTMLSpanElement;
  private elSubheader!: HTMLSpanElement;
  private elDesc!: HTMLDivElement;
  private elChipRow1!: HTMLDivElement;
  private elChipRow2!: HTMLDivElement;
  private elFooter!: HTMLDivElement;
  private elBadge!: HTMLDivElement;
  private currentIconId = "";
  private currentAccent = "";
  private currentBadgeColor = "";
  private currentBg = "";
  private currentDescMarkdown = "";

  set arch(v: ArchLike | null) { this.archData = v; this.render(); }
  get arch(): ArchLike | null { return this.archData; }

  connectedCallback() {
    this.build();
    adoptWidgetsSheet(this.root!);
    this.render();
  }
  attributeChangedCallback() { this.render(); }

  private build() {
    if (this.built) return;
    this.built = true;
    const root = this.root ?? this.attachShadow({ mode: "open" });
    this.root = root;

    const style = document.createElement("style");
    style.textContent = STYLE;
    root.append(style);

    const card = document.createElement("div");
    card.className = "card";
    card.setAttribute("part", "card");

    // Header: icon + (name + subheader)
    const header = document.createElement("div");
    header.className = "header";
    header.setAttribute("part", "header");

    // Icon belongs to the title — render it INSIDE the title row next
    // to the name so it visually reads as part of the name. Subheader
    // sits BELOW that row, not next to the icon, so the icon doesn't
    // float in a weird gap between name and subheader.
    const titleRow = document.createElement("div");
    titleRow.className = "title-row";

    const icon = document.createElement("div");
    icon.className = "icon";
    icon.setAttribute("part", "icon");
    const iconDefault = document.createElement("span");
    iconDefault.className = "icon-default";
    const iconSlot = document.createElement("slot");
    iconSlot.setAttribute("name", "icon");
    icon.append(iconSlot, iconDefault);
    this.elIcon = icon;
    this.elIconDefault = iconDefault;

    const name = document.createElement("span");
    name.className = "name";
    name.setAttribute("part", "name");
    this.elName = name;

    titleRow.append(icon, name);

    const subheader = document.createElement("span");
    subheader.className = "subheader";
    subheader.setAttribute("part", "subheader");
    this.elSubheader = subheader;

    const nameBlock = document.createElement("div");
    nameBlock.className = "name-block";
    nameBlock.append(titleRow, subheader);

    header.append(nameBlock);

    // Body: description + two chip rows (collapsing) for leaf mode,
    // plus a default <slot> whose projected content becomes the
    // subspace overlay in subspace mode. CSS hides whichever set is
    // inactive for the current data-mode.
    const body = document.createElement("div");
    body.className = "body";
    body.setAttribute("part", "body");

    const desc = document.createElement("div");
    desc.className = "desc";
    desc.setAttribute("part", "description");
    this.elDesc = desc;

    const chipRow1 = document.createElement("div");
    chipRow1.className = "chip-row";
    chipRow1.setAttribute("part", "chip-row");
    chipRow1.dataset.slot = "label1";
    this.elChipRow1 = chipRow1;

    const chipRow2 = document.createElement("div");
    chipRow2.className = "chip-row";
    chipRow2.setAttribute("part", "chip-row");
    chipRow2.dataset.slot = "label2";
    this.elChipRow2 = chipRow2;

    const subspace = document.createElement("div");
    subspace.className = "subspace";
    subspace.setAttribute("part", "subspace");
    const slot = document.createElement("slot");
    subspace.append(slot);

    body.append(desc, chipRow1, chipRow2, subspace);

    // Footer.
    const footer = document.createElement("div");
    footer.className = "footer";
    footer.setAttribute("part", "footer");
    this.elFooter = footer;

    // Corner badge — identity tag (e.g. cloud provider). Appended last
    // so it stacks above the header content.
    const badge = document.createElement("div");
    badge.className = "badge";
    badge.setAttribute("part", "badge");
    this.elBadge = badge;

    card.append(header, body, footer, badge);
    root.append(card);
  }

  private render() {
    if (!this.built) return;
    const arch = this.archData;
    const name = this.getAttribute("name") ?? arch?.name ?? "";
    const typeName = arch?.typeName ?? "";
    const description = arch?.description ?? "";

    // Split merged casing/snake into whitespace-separated words so the
    // header can wrap the name across lines at low zoom (e.g. dot
    // zoom, where the card is tiny and the name needs to break to
    // fit). Casing preserved — see `splitCasedName`.
    const displayName = splitCasedName(name);
    if (this.elName.textContent !== displayName) this.elName.textContent = displayName;

    // Unnamed module (§4.14): the name was derived from its type, not
    // authored. Flag the host so CSS can mute/italicise the name, and add
    // a tooltip explaining the cue. `data-unnamed` is not observed, so
    // setting it here doesn't re-enter render().
    const unnamed = arch?.unnamed === true;
    if (unnamed) {
      if (this.getAttribute("data-unnamed") !== "true") {
        this.setAttribute("data-unnamed", "true");
        this.title = "Name derived from type (unnamed module, §4.14)";
      }
    } else if (this.hasAttribute("data-unnamed")) {
      this.removeAttribute("data-unnamed");
      if (this.title.startsWith("Name derived from type")) this.title = "";
    }

    // Icon: `widget.icon` attr, falling back to the universal "module"
    // glyph. ArchLang-resolved modules always carry an icon (stamped
    // from the `module` base kind or a subtype override); standalone
    // uses of the element with no attr get the fallback.
    const iconId = this.getAttribute("icon") ?? "module";
    if (iconId !== this.currentIconId) {
      this.currentIconId = iconId;
      this.elIconDefault.innerHTML = iconSvg(iconId);
    }

    // Accent: `widget.color` attr, else the legacy `arch.accent` field
    // escape hatch, else the same fallback the builtin module-kind
    // defaults use.
    const colorAttr = this.getAttribute("color");
    const accent = paletteColor(colorAttr || findField(arch, ["accent"]) || "info");
    if (accent !== this.currentAccent) {
      this.currentAccent = accent;
      this.style.setProperty("--arch-module-accent", accent);
    }

    // Background: `widget.bg` overrides the default gradient.
    const bgAttr = this.getAttribute("bg") ?? "";
    if (bgAttr !== this.currentBg) {
      this.currentBg = bgAttr;
      if (bgAttr) this.style.setProperty("--arch-module-bg", paletteColor(bgAttr));
      else this.style.removeProperty("--arch-module-bg");
    }

    // Subheader: configurable label/field path; falls back to typeName
    // when unset so the widget looks reasonable on bare modules.
    // The attribute IS the value: a getter (`widget.subheader: @@team`) was
    // resolved in the engine, per node, after cascade.
    const subheaderText = this.getAttribute("subheader") ?? typeName;
    if (this.elSubheader.textContent !== subheaderText) {
      this.elSubheader.textContent = subheaderText;
    }

    // Footer: configurable.
    const footerText = this.getAttribute("footer") ?? "";
    if (this.elFooter.textContent !== footerText) {
      this.elFooter.textContent = footerText;
    }

    // Badge: short literal identity tag (cloud provider, tier, …). Plain
    // text, not a getter — the cascade sets the literal directly. When no
    // widget badge is set, fall back to the module's space (v0.5) so
    // space-scoped nodes carry a visible `Corporate.Loans`-style tag.
    const explicitBadge = this.getAttribute("badge");
    const badgeText =
      explicitBadge != null && explicitBadge !== ""
        ? explicitBadge
        : (arch?.space ?? "");
    if (this.elBadge.textContent !== badgeText) {
      this.elBadge.textContent = badgeText;
    }
    const badgeColor = this.getAttribute("badge-color") ?? "";
    if (badgeColor !== this.currentBadgeColor) {
      this.currentBadgeColor = badgeColor;
      if (badgeColor) this.style.setProperty("--_badge", paletteColor(badgeColor));
      else this.style.removeProperty("--_badge");
    }

    // Body mode — subspace vs leaf. The renderer stamps
    // `data-has-subspace` on the host whenever the item carries a
    // non-empty `BoardItem.space` (= what actually triggers the
    // subspace overlay). Use that instead of introspecting
    // `arch.children` because parser fallbacks can synthesise phantom
    // children that aren't real subspace nodes.
    const hasSubspace = this.getAttribute("data-has-subspace") === "true";
    const mode = hasSubspace ? "subspace" : "leaf";
    if (this.getAttribute("data-mode") !== mode) {
      this.setAttribute("data-mode", mode);
    }

    // Description shown only in leaf mode — rendered as markdown so
    // newlines, headings, lists, code, etc. behave like the side panel.
    const descMarkdown = hasSubspace ? "" : description;
    if (descMarkdown !== this.currentDescMarkdown) {
      this.currentDescMarkdown = descMarkdown;
      this.elDesc.innerHTML = renderDescriptionHtml(
        preprocessWidgetDescription(descMarkdown),
      );
    }
    const descText = descMarkdown; // for empty-state detection

    // Chip slots: only in leaf mode.
    let chipsCount = 0;
    if (!hasSubspace) {
      chipsCount += this.renderChipRow(this.elChipRow1, "label1");
      chipsCount += this.renderChipRow(this.elChipRow2, "label2");
    } else {
      this.elChipRow1.replaceChildren();
      this.elChipRow2.replaceChildren();
    }
    if (this.getAttribute("data-chips") !== String(chipsCount)) {
      this.setAttribute("data-chips", String(chipsCount));
    }

    // Empty-state shrinks padding.
    const empty =
      !subheaderText &&
      !descText &&
      !footerText &&
      chipsCount === 0 &&
      !hasSubspace;
    if (empty) this.setAttribute("data-empty", "true");
    else this.removeAttribute("data-empty");
  }

  /** Render a chip into `host` from the `widget.<slotAttr>` reference.
   *  Returns 1 if a chip was rendered, 0 otherwise. */
  private renderChipRow(host: HTMLDivElement, slotAttr: string): number {
    const value = this.getAttribute(slotAttr);
    if (!value) { host.replaceChildren(); return 0; }
    // The key a chip labels its value with rides along as `<slot>Key`, from the
    // getter the value was resolved from (`extractWidgetSpec`).
    const key = this.getAttribute(`${slotAttr}Key`) ?? "";

    let chip = host.firstElementChild as HTMLElement | null;
    if (!chip || chip.className !== "chip") {
      host.replaceChildren();
      chip = document.createElement("span");
      chip.className = "chip";
      chip.setAttribute("part", "chip");
      const k = document.createElement("span");
      k.className = "chip-key";
      const v = document.createElement("span");
      v.className = "chip-value";
      chip.append(k, v);
      host.append(chip);
    }
    const kEl = chip.querySelector(".chip-key") as HTMLElement;
    const vEl = chip.querySelector(".chip-value") as HTMLElement;
    if (kEl.textContent !== key) kEl.textContent = key;
    if (vEl.textContent !== value) vEl.textContent = value;
    return 1;
  }
}

/* ─────────────── arch-template ─────────────── */

const TEMPLATE_TAG = "arch-template";

class ArchTemplateElement extends HTMLElement {
  private archData: ArchLike | null = null;
  private shadow: ShadowRoot | null = null;
  private pendingHtml: string | null = null;

  set arch(v: ArchLike | null) {
    this.archData = v;
    this.dispatchEvent(
      new CustomEvent("arch-model-updated", { bubbles: true, composed: true })
    );
  }
  get arch(): ArchLike | null { return this.archData; }

  connectedCallback() {
    this.shadow ??= this.attachShadow({ mode: "open" });
    adoptWidgetsSheet(this.shadow);
    registerTemplateShadow(this.shadow);
    if (this.pendingHtml !== null) {
      this.shadow.innerHTML = this.pendingHtml;
      this.pendingHtml = null;
    }
  }

  disconnectedCallback() {
    if (this.shadow) unregisterTemplateShadow(this.shadow);
  }

  setTemplate(html: string) {
    if (!this.shadow) {
      this.pendingHtml = html;
      return;
    }
    this.shadow.innerHTML = html;
  }
}

/* ─────────────── shared widgets stylesheet ─────────────── */

const widgetsSheet: CSSStyleSheet | null =
  typeof CSSStyleSheet !== "undefined" ? new CSSStyleSheet() : null;
const liveTemplateShadows = new Set<ShadowRoot>();

function adoptWidgetsSheet(shadow: ShadowRoot): void {
  if (!widgetsSheet) return;
  const current = shadow.adoptedStyleSheets;
  if (!Array.isArray(current) && !(current && typeof current[Symbol.iterator] === "function")) return;
  const arr = Array.from(current);
  if (arr.includes(widgetsSheet)) return;
  shadow.adoptedStyleSheets = [...arr, widgetsSheet];
}

function registerTemplateShadow(shadow: ShadowRoot): void {
  liveTemplateShadows.add(shadow);
}

function unregisterTemplateShadow(shadow: ShadowRoot): void {
  liveTemplateShadows.delete(shadow);
}

export function setWidgetsCss(css: string): void {
  if (widgetsSheet) {
    widgetsSheet.replaceSync(css);
    if (typeof document !== "undefined" && Array.isArray(document.adoptedStyleSheets)) {
      if (!document.adoptedStyleSheets.includes(widgetsSheet)) {
        document.adoptedStyleSheets = [...document.adoptedStyleSheets, widgetsSheet];
      }
    }
    return;
  }
  if (typeof document === "undefined") return;
  let style = document.getElementById("arch-widgets-css") as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = "arch-widgets-css";
    document.head.append(style);
  }
  style.textContent = css;
}

const ASPECT_TAG = "arch-aspect";

/**
 * Small label node for the "aspect web" overlay — a coloured pill, not a
 * module card. Reads `name` (the aspect value) and `color` (the aspect's
 * stable colour) attributes. Fixed compact size via static dims.
 */
class ArchAspectElement extends HTMLElement {
  static archWidth = 150;
  static archHeight = 44;
  static get observedAttributes() {
    return ["name", "color"];
  }
  private root: ShadowRoot | null = null;
  private label: HTMLSpanElement | null = null;

  connectedCallback() {
    this.build();
    this.render();
  }
  attributeChangedCallback() {
    if (this.root) this.render();
  }
  private build() {
    if (this.root) return;
    this.root = this.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent =
      ":host{display:block;width:100%;height:100%}" +
      ".pill{box-sizing:border-box;width:100%;height:100%;display:flex;align-items:center;justify-content:center;gap:6px;" +
      "padding:4px 10px;border-radius:999px;font:600 12px/1.2 ui-sans-serif,system-ui,sans-serif;color:#e5e7eb;" +
      "background:color-mix(in srgb,var(--_c,#94a3b8) 22%,#0b1220);border:1px solid var(--_c,#94a3b8);box-shadow:0 1px 6px rgba(0,0,0,.4)}" +
      ".dot{width:8px;height:8px;border-radius:999px;background:var(--_c,#94a3b8);flex:none}" +
      ".txt{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}";
    const pill = document.createElement("div");
    pill.className = "pill";
    const dot = document.createElement("span");
    dot.className = "dot";
    this.label = document.createElement("span");
    this.label.className = "txt";
    pill.append(dot, this.label);
    this.root.append(style, pill);
  }
  private render() {
    if (!this.root || !this.label) return;
    this.style.setProperty("--_c", this.getAttribute("color") || "#94a3b8");
    this.label.textContent = this.getAttribute("name") ?? "";
  }
}

const SCENE_TAG = "arch-scene-module";

/* ───────── shared scene host stylesheet (M3 — light DOM) ─────────
 * The scene widget renders in LIGHT DOM (no per-node shadow root, no per-node
 * `<style>`): its `toHTML` output is fully inline-styled, so nothing needs
 * isolating. The host chrome (base box + hover/selected/highlight/error rings)
 * that used to live in each shadow's `<style>` moves here — ONE document sheet,
 * keyed on the tag + `data-*`, driven by per-node CSS custom props the element
 * sets in `render()` (radius + ring colours, which depend on the node's accent
 * and the active theme). `line-height:normal` is pinned so light-DOM card text
 * doesn't inherit the document body's line-height (shadow used to block that). */
// The state glow (hover / highlight / error / selected) rides a `::after`
// overlay whose OPACITY is what animates — never `box-shadow` itself.
// box-shadow (a blurred paint) is not compositable, so a `transition:box-shadow`
// forces a main-thread repaint of the blurred region every frame of the fade,
// on the same content layer the particle-flow + pulse effects composite into —
// that repaint is what made "other effects freeze while a node is hovered".
// Here the shadow is painted ONCE into the overlay; the fade transitions only
// `opacity` (compositor-only, the overlay gets its own layer during the
// animation), and a state switch (e.g. hover→selected) just swaps the colour
// vars for a single repaint instead of a per-frame box-shadow interpolation.
const SCENE_HOST_CSS =
  `${SCENE_TAG}{display:block;position:relative;width:100%;height:100%;cursor:pointer;line-height:normal;` +
  `border-radius:var(--au-radius,0);` +
  `--au-ring-w:calc(1px / max(var(--arch-scale, 1), 0.15));` +
  `--au-ring-glow:calc(18px / max(var(--arch-scale, 1), 0.3))}` +
  `${SCENE_TAG}::after{content:"";position:absolute;inset:0;border-radius:inherit;pointer-events:none;opacity:0;` +
  `box-shadow:0 0 0 var(--au-glow-w, var(--au-ring-w)) var(--au-glow-c, transparent), 0 0 var(--au-ring-glow) -4px var(--au-glow-g, transparent);` +
  `transition:opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)}` +
  `${SCENE_TAG}[data-hovered="true"]::after{--au-glow-c:var(--au-ring-hc);--au-glow-g:var(--au-ring-hg);opacity:1}` +
  `${SCENE_TAG}[data-highlighted="true"]::after{--au-glow-c:var(--au-ring-lc);--au-glow-g:var(--au-ring-lg);opacity:1}` +
  `${SCENE_TAG}[data-error="true"]::after{--au-glow-c:var(--au-ring-ec);--au-glow-g:var(--au-ring-eg);opacity:1}` +
  `${SCENE_TAG}[data-selected="true"]::after{--au-glow-w:calc(var(--au-ring-w) * 2);--au-glow-c:var(--au-ring-sc);--au-glow-g:var(--au-ring-sg);opacity:1}`;

let sceneHostSheet: CSSStyleSheet | null = null;
let sceneHostStyleEl: HTMLStyleElement | null = null;
/** Install the shared scene host stylesheet ONCE (idempotent). Prefers a
 *  constructable `adoptedStyleSheets`; falls back to a `<style>` in head (jsdom
 *  without adopted sheets). */
function ensureSceneHostStyles(): void {
  if (typeof document === "undefined") return;
  const canAdopt =
    typeof CSSStyleSheet !== "undefined" &&
    Array.isArray((document as Document).adoptedStyleSheets);
  if (canAdopt) {
    if (!sceneHostSheet) {
      sceneHostSheet = new CSSStyleSheet();
      sceneHostSheet.replaceSync(SCENE_HOST_CSS);
    }
    if (!document.adoptedStyleSheets.includes(sceneHostSheet)) {
      document.adoptedStyleSheets = [...document.adoptedStyleSheets, sceneHostSheet];
    }
    return;
  }
  if (!sceneHostStyleEl) {
    sceneHostStyleEl = document.createElement("style");
    sceneHostStyleEl.id = "arch-scene-host-css";
    sceneHostStyleEl.textContent = SCENE_HOST_CSS;
    document.head.append(sceneHostStyleEl);
  }
}

/**
 * `arch-scene-module` — proof-of-concept dual-render widget.
 *
 * Unlike `arch-module` (hand-written innerHTML + CSS), this element builds
 * a DOM-free **scene** via `composeModuleCard` and paints it with the
 * `@archlang/scene` HTML backend (`toHTML`). The SAME scene + compose
 * function feed the headless SVG backend (`toSVG`) used for server-side
 * render / portable export — so the live face and the export cannot drift.
 *
 * Configured like `arch-module`'s core fields: `name` (or model name),
 * `color`/`accent` (stroke — a palette name or a literal), `icon` (header
 * glyph), `subheader`/`footer` (values, already resolved by the engine). This is
 * the P2 end-to-end test of the scene → HTML path on the live board.
 */
class ArchSceneModuleElement extends HTMLElement {
  /** Customisable size: honor widget.width / widget.height when numeric;
   *  otherwise fall through to the solver's NODE_W / NODE_H defaults (the
   *  same geometry the old default widget produced). */
  static sizeFor(props: ReadonlyMap<string, unknown>): { w?: number; h?: number } {
    const w = props.get("width");
    const h = props.get("height");
    const out: { w?: number; h?: number } = {};
    if (typeof w === "number") out.w = w;
    if (typeof h === "number") out.h = h;
    return out;
  }
  static get observedAttributes() {
    return ["name", "color", "accent", "icon", "bg", "shape", "subheader", "footer", "dashed", "label1", "label2", "badge", "badge-color", "anchors", "classification", "width", "height", "data-aspect", "data-has-subspace", "data-lifecycle", "data-lifecycle-badge"];
  }

  private archData: ArchLike | null = null;
  /** Memo of the last painted light-DOM HTML — skips the innerHTML rewrite when
   *  nothing visual changed (see `render`). */
  private lastHtml: string | null = null;
  /** Theme repaint is HOISTED off the per-node path. Instead of every element
   *  subscribing to `themechange` (N nodes = N window listeners — the O(N) cost
   *  a catalog of embeds pays), ONE static subscription fans out to all live
   *  scene elements. Cards bake concrete colours from the active data-theme (see
   *  themeTokens), so a swap must recompose them (CSS-var chrome updates itself;
   *  the scene HTML does not). `live` is the connected-element registry; the sub
   *  arms on the first connect and tears down when the last element leaves — so
   *  an idle board, and a catalog of N embeds, hold exactly ONE themechange
   *  listener regardless of node count. */
  private static live = new Set<ArchSceneModuleElement>();
  private static themeUnsub: (() => void) | null = null;
  private static ensureThemeSub(): void {
    ArchSceneModuleElement.themeUnsub ??= subscribeTheme(() => {
      for (const el of ArchSceneModuleElement.live) el.render();
    });
  }

  set arch(v: ArchLike | null) {
    // Same-ref guard. WidgetNode re-assigns `el.arch = module` on every 0.05
    // zoom step (scale is in its effect deps); the module ref is stable across
    // zoom. Without this guard each no-op re-assign rewrites the whole shadow
    // via innerHTML, which DETACHES the node Safari latched at gesturestart —
    // killing an in-flight pinch after a single gesturechange (the
    // pinch-into-subspace-dies bug), and needlessly re-composing every frame.
    // Model edits produce a FRESH object (immutable-model contract — must
    // survive the deferred delta-compiler live wiring), so a real change still
    // re-renders.
    if (v === this.archData) return;
    this.archData = v;
    this.render();
  }
  get arch(): ArchLike | null {
    return this.archData;
  }

  connectedCallback() {
    ensureSceneHostStyles();
    ArchSceneModuleElement.live.add(this);
    ArchSceneModuleElement.ensureThemeSub();
    this.render();
  }
  disconnectedCallback() {
    ArchSceneModuleElement.live.delete(this);
    // Last element out turns the shared subscription off — no idle listener.
    if (ArchSceneModuleElement.live.size === 0) {
      ArchSceneModuleElement.themeUnsub?.();
      ArchSceneModuleElement.themeUnsub = null;
    }
  }
  attributeChangedCallback() {
    // Light DOM: gate on connectivity (there is no shadow sentinel). Observed
    // attrs the render reads directly — data-aspect (node colour), width/height,
    // data-has-subspace (body↔slot mode), color/badge/subheader/label* — change
    // while `el.arch` stays a stable ref, so this MUST repaint them.
    if (this.isConnected) this.render();
  }

  private render() {
    const arch = this.archData;
    // Nothing to paint until the host sets `el.arch` (connectedCallback fires a
    // render before data arrives). Guard the null so the pure mapping never
    // dereferences an absent module.
    if (!arch) return;
    // The board's unified node colour (aspect / label / diff) arrives as
    // `data-aspect`; it wins over widget.color but isn't a model field, so it
    // rides `accentOverride` into the shared composer rather than the props.
    const aspect = this.getAttribute("data-aspect") || undefined;
    const subspace = this.getAttribute("data-has-subspace") === "true";
    // Actual node size — proportional content scales to it (viewBox-style).
    const width = Number(this.getAttribute("width")) || NODE_W;
    const height = Number(this.getAttribute("height")) || NODE_H;
    const resolveIcon = (id: string) =>
      iconSvg(id, 24).replace(/^<svg[^>]*>/, "").replace(/<\/svg>$/, "") || undefined;
    // `makeTokens: themeTokens` — colour the card from the active data-theme CSS
    // vars (the headless export path omits it and stays on defaultTokens).
    const cardOpts = { resolveIcon, subspace, accentOverride: aspect, makeTokens: themeTokens };
    // Derive spec/input/tokens ONCE — for the host chrome here AND, via
    // `precomputed`, inside the shared composer (which would otherwise recompute
    // all three). Single source of truth with the headless/server path, no drift.
    const spec = extractWidgetSpec(arch as unknown as Module);
    const input = moduleToCardInput(arch as unknown as Module, spec.props, cardOpts);
    const accent = input.accent ?? "#60a5fa";
    const tokens = themeTokens(accent);

    // Unnamed module (§4.14): the card renders the name italic + muted (in
    // both backends); the host carries the explanatory tooltip + the
    // data-unnamed hook, matching the arch-module contract.
    if (input.unnamed) {
      this.setAttribute("data-unnamed", "true");
      this.title = "Name derived from type (unnamed module, §4.14)";
    } else if (this.hasAttribute("data-unnamed")) {
      this.removeAttribute("data-unnamed");
      this.removeAttribute("title");
    }

    // Dispatch through the SHARED composer so the live board and the headless
    // SVG export render the identical scene — including shape widgets (a
    // database cylinder, a table). `composeBoardNode` reads the module's own
    // `widget:` tag to pick the composition; a plain module falls to the card.
    // `aspectAccent` (aspect colouring) overrides widget.color via props above.
    const item = { id: "", label: "", x: 0, y: 0, w: width, h: height, meta: arch as unknown as Module } as BoardItem;
    // Lifecycle state arrives as an attribute, exactly as the unified node
    // colour does — so the live board and the headless export run the SAME
    // composer with the same input and cannot drift.
    const lcState = this.getAttribute("data-lifecycle") as LifecyclePaint["state"] | null;
    const lifecycle: LifecyclePaint | undefined = lcState
      ? { state: lcState, badge: this.getAttribute("data-lifecycle-badge") ?? undefined }
      : undefined;
    const node = composeBoardNode(item, { ...cardOpts, precomputed: { spec, input, tokens }, lifecycle });

    // Host chrome (radius + state-ring colours) rides per-node CSS custom props
    // consumed by the shared SCENE_HOST_CSS sheet — no per-node <style>. Set
    // BEFORE the html memo guard (a var write is on the host style, not innerHTML,
    // so it never detaches a live gesture target). No host background: the rounded
    // card paints its own; a square opaque wrapper would mask the corners + ring.
    const radius = tokens.radiusMd * (height / 170);
    this.applyHostVars(tokens, accent, radius);
    // Light DOM: the card content is fully inline-styled, so it needs no shadow
    // isolation. The scene widget projects no nested space here (the board renders
    // it as a positioned sibling — see SpaceRenderer), so this element has no React
    // children and `this.innerHTML` never clobbers any.
    const html = node ? toHTML(node) : "";
    // Skip the rewrite when the composed HTML is byte-identical (a redundant
    // attributeChangedCallback, or an early paint before data lands). The innerHTML
    // write is what detaches a live node; guarding it keeps a Safari gesture target
    // attached through no-op renders.
    if (html === this.lastHtml) return;
    this.lastHtml = html;
    this.innerHTML = html;
  }

  /** Push the per-node host-chrome values (border radius + the four state-ring
   *  colour pairs) as CSS custom props the shared SCENE_HOST_CSS sheet reads.
   *  Same values the old shadow `sceneHostStyle` baked: hover = accent softened
   *  toward the theme bg; highlight/error/selected = fixed hues with a bg-mixed
   *  glow. React never manages these `--au-*` props, so its style reconciliation
   *  leaves them intact. */
  private applyHostVars(tokens: ReturnType<typeof defaultTokens>, accent: string, radius: number): void {
    const s = this.style;
    s.setProperty("--au-radius", `${radius}px`);
    const accentSoft = mix(accent, tokens.bg, 0.55);
    s.setProperty("--au-ring-hc", accentSoft);
    s.setProperty("--au-ring-hg", accentSoft);
    s.setProperty("--au-ring-lc", "#facc15");
    s.setProperty("--au-ring-lg", mix("#facc15", tokens.bg, 0.5));
    s.setProperty("--au-ring-ec", "#f87171");
    s.setProperty("--au-ring-eg", mix("#f87171", tokens.bg, 0.55));
    s.setProperty("--au-ring-sc", "#60a5fa");
    s.setProperty("--au-ring-sg", mix("#60a5fa", tokens.bg, 0.45));
  }
}

export function registerBuiltinWidgets(): void {
  if (registered) return;
  registered = true;
  if (typeof customElements === "undefined") return;
  if (!customElements.get(TAG)) customElements.define(TAG, ArchModuleElement);
  if (!customElements.get(TEMPLATE_TAG)) customElements.define(TEMPLATE_TAG, ArchTemplateElement);
  if (!customElements.get(ASPECT_TAG)) customElements.define(ASPECT_TAG, ArchAspectElement);
  if (!customElements.get(SCENE_TAG)) customElements.define(SCENE_TAG, ArchSceneModuleElement);
  // Content-sized shapes (arch-note grows to its text) size via the shared
  // scene metrics; every other tag keeps its custom-element `sizeFor`. Wrapping
  // the custom-element registry makes the live board's note geometry identical
  // to the headless export (which wraps the explicit registry the same way).
  setWidgetSizeRegistry(shapeSizeRegistry(customElementsRegistry));
}

/** The default module face: the scene-based dual-render widget (one scene →
 *  live HTML board + portable headless SVG). The legacy hand-written
 *  `arch-module` stays registered as an explicit `widget: arch-module` opt-in. */
export const BUILTIN_MODULE_TAG = SCENE_TAG;
export const BUILTIN_TEMPLATE_TAG = TEMPLATE_TAG;
export const BUILTIN_ASPECT_TAG = ASPECT_TAG;

/**
 * Widget tags rendered by the scene element instead of their stdlib
 * custom element, so the live board and the headless SVG export share ONE
 * composition (`composeBoardNode` dispatches on the module's own tag). The
 * stdlib elements stay registered for the LAYOUT solver's `sizeFor` (keyed on
 * the real tag), which is why the tag is remapped for render only.
 */
const SCENE_SHAPE_TAGS: ReadonlyMap<string, string> = new Map([
  // The engine cascades `widget: arch-module` onto EVERY module (base-type
  // default), so this remap is what actually makes the scene card the default
  // face on the live board — the empty-tag fallback below never fires for a
  // real, resolved module. The legacy hand-written `arch-module` element stays
  // registered (harmless) but no longer renders.
  ["arch-module", SCENE_TAG],
  ["arch-backend-database", SCENE_TAG],
  ["arch-table", SCENE_TAG],
  ["arch-state", SCENE_TAG],
  ["arch-state-initial", SCENE_TAG],
  ["arch-state-final", SCENE_TAG],
  ["arch-note", SCENE_TAG],
  ["arch-class", SCENE_TAG],
  ["arch-enum", SCENE_TAG],
  ["arch-c4-person", SCENE_TAG],
  ["arch-c4-external-person", SCENE_TAG],
  ["arch-c4-boundary", SCENE_TAG],
  ["arch-c4-external-system", SCENE_TAG],
  // arch.extras device / actor figures.
  ["arch-user", SCENE_TAG],
  ["arch-usergroup", SCENE_TAG],
  ["arch-webpage", SCENE_TAG],
  ["arch-laptop", SCENE_TAG],
  ["arch-tablet", SCENE_TAG],
  ["arch-smartphone", SCENE_TAG],
  ["arch-iphone", SCENE_TAG],
  ["arch-android", SCENE_TAG],
  ["arch-desktop", SCENE_TAG],
  ["arch-server", SCENE_TAG],
  ["arch-cloud", SCENE_TAG],
]);

/** Remap a widget tag to its scene renderer, or return it unchanged. */
export function sceneRenderTag(tag: string): string {
  return SCENE_SHAPE_TAGS.get(tag) ?? tag;
}
